import React, { useState } from 'react';
import { X, MapPin, Save, Loader2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

const AddressEditModal = ({ isOpen, onClose, order, onUpdate }) => {
    const [address, setAddress] = useState(order?.deliveryAddress || order?.customer_address || '');
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [useAutocomplete, setUseAutocomplete] = useState(true); // Default to autocomplete mode

    // Update local state when order changes
    React.useEffect(() => {
        if (order) {
            setAddress(order.deliveryAddress || order.customer_address || '');
            setNotes('');
            setUseAutocomplete(true);
        }
    }, [order]);

    if (!isOpen || !order) return null;

    const handleAddressSelect = (selection) => {
        console.log("AddressEditModal - Selected:", selection);
        setAddress(selection.address);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!address || address.trim().length < 5) {
            toast.error('La dirección es muy corta');
            return;
        }

        try {
            setIsSaving(true);
            await onUpdate(order.id, address, notes);
            onClose();
            toast.success('Dirección actualizada correctamente');
        } catch (error) {
            console.error('Error upgrading address:', error);
            const msg = error.response?.data?.message || 'Error al actualizar la dirección';
            toast.error(msg);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-scaleIn">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                            <MapPin className="w-5 h-5" />
                        </div>
                        <h3 className="font-bold text-gray-800">
                            Editar Dirección
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-5">

                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                                Dirección de Entrega
                            </label>
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100 font-medium">
                                Pedido #{order.orderNumber || order.order_number}
                            </span>
                        </div>

                        {/* Toggle between autocomplete and manual */}
                        <div className="flex gap-1 mb-3">
                            <button
                                type="button"
                                onClick={() => setUseAutocomplete(true)}
                                className={`flex-1 text-xs py-1.5 px-3 rounded-lg font-bold transition-all flex items-center justify-center gap-1 ${useAutocomplete
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                            >
                                <Search className="w-3 h-3" />
                                Buscar en Mapa
                            </button>
                            <button
                                type="button"
                                onClick={() => setUseAutocomplete(false)}
                                className={`flex-1 text-xs py-1.5 px-3 rounded-lg font-bold transition-all flex items-center justify-center gap-1 ${!useAutocomplete
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                            >
                                ✏️ Escribir Manual
                            </button>
                        </div>

                        {useAutocomplete ? (
                            <div>
                                {/* Google Places Autocomplete */}
                                <div className="relative" style={{ zIndex: 1000 }}>
                                    <GooglePlacesAutocomplete
                                        onSelect={handleAddressSelect}
                                        placeholder="Buscar dirección en Bogotá..."
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1.5 flex items-center justify-between">
                                    <span>* Escribe para buscar y selecciona una sugerencia.</span>
                                    <span className="text-gray-300">Google Maps</span>
                                </p>
                                {/* Show currently selected address */}
                                {address && (
                                    <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                                        <p className="text-[10px] font-bold text-green-600 uppercase mb-0.5">Dirección Seleccionada:</p>
                                        <p className="text-xs text-green-800 font-medium">{address}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                {/* Manual textarea */}
                                <textarea
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[80px] text-gray-800 font-medium resize-none shadow-sm"
                                    placeholder="Ej: Calle 123 # 45 - 67, Apto 201"
                                    autoFocus
                                />
                                <p className="text-[10px] text-gray-400 mt-1">
                                    * Al guardar, la ubicación en el mapa se reiniciará para geocodificar de nuevo.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="mb-6">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                            Nota / Referencia (Opcional)
                        </label>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Ej: Casa esquinera, reja blanca..."
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-bold transition-colors"
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving || !address || address.trim().length < 5}
                            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Guardar Cambios
                                </>
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default AddressEditModal;
